import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Icon,
  Image,
  LinkBox,
  LinkOverlay,
  Text,
  AspectRatio,
  useDimensions,
} from "@chakra-ui/react";
import { BiChevronLeft, BiChevronRight, BiWorld } from "react-icons/bi";
import SocialAccountPropType from "../../../_types//SocialAccount";
import PostPropType from "../../../_types/Post";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import * as SocialProviders from "../../../_constants/SocialProvider";
import { getUrl } from "../../../_utils/url";
import { uniqueId } from "../../../_utils/uniqueId";
import { formatPostMessage } from "../../../_utils/postMessageFormatter";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import {
  getFixedPhotosVideosOrder,
  getMediaFileMeta,
} from "../PostEditor/helpers";

const SocialPostCardFacebook = ({
  account,
  post,
  time,
  ...props
}) => {
  const { t } = useTranslation(["planner"]);
  const carouselInnerRef = useRef(null);
  const carouselListBoxModel = useDimensions(carouselInnerRef, true);
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);
  const [carouselItems, setCarouselItems] = useState([]);

  const [isSmallScraperImage, setIsSmalScraperImage] = useState(true);
  const [isMediaHorizontal, setIsMediaHorizontal] = useState(false);

  const type = account?.social_type;

  const initScraperMediaMeta = useCallback(async () => {
    if (post?.scraper?.image) {
      const meta = await getMediaFileMeta("image", post?.scraper?.image);
      const isLarge = meta.width >= 400 && meta.height >= 400;

      setIsSmalScraperImage(!isLarge);
    } else {
      setIsSmalScraperImage(true);
    }
  }, [post?.scraper?.image, setIsSmalScraperImage]);

  const initMediaMeta = useCallback(async () => {
    if (post?.videos?.length) {
      if (post.videos.length === 1) {
        const item = post.videos[0];
        const meta =
          item.meta?.width && item.meta?.height
            ? item.meta
            : await getMediaFileMeta("video", item.url);

        setIsMediaHorizontal(meta.width / meta.height > 1);
      } else {
        setIsMediaHorizontal(false);
      }
    } else if (post?.photos?.length) {
      if (post.photos.length === 1) {
        const item = post.photos[0];
        const meta =
          item.meta?.width && item.meta?.height
            ? item.meta
            : await getMediaFileMeta("image", item.url);

        setIsMediaHorizontal(meta.width / meta.height > 1);
      } else {
        setIsMediaHorizontal(false);
      }
    } else {
      setIsMediaHorizontal(false);
    }
  }, [post?.photos, post?.videos, setIsMediaHorizontal]);

  const handleCarouselPrevClick = useCallback(() => {
    setCarouselActiveIndex(carouselActiveIndex - 1);
  }, [carouselActiveIndex, setCarouselActiveIndex]);

  const handleCarouselNextClick = useCallback(() => {
    setCarouselActiveIndex(carouselActiveIndex + 1);
  }, [carouselActiveIndex, setCarouselActiveIndex]);

  const carousel = useMemo(() => {
    const additional = post?.additional || [];

    return additional.find(
      (item) =>
        item.source === SocialProviders.FACEBOOK &&
        item.type === PostAdditionalTypes.CAROUSEL
    );
  }, [post]);

  useEffect(() => {
    initScraperMediaMeta();
  }, [post?.scraper?.image]);

  useEffect(() => {
    initMediaMeta();
  }, [post?.photos, post?.videos]);

  useEffect(() => {
    const items =
      carousel?.items?.map((item) => ({ ...item, $id: uniqueId() })) || [];

    if (carousel?.endCard) {
      const link = carousel?.endCardLink || "";
      const url = getUrl(link);
      const $id = uniqueId();

      items.push({
        $id,
        isEndCard: true,
        host: url?.host || "",
        link,
        image: account?.image || null,
        title: "",
        description: "",
      });
    }

    setCarouselItems(items);
  }, [
    carousel?.items,
    carousel?.endCard,
    carousel?.endCardLink,
    account?.image,
  ]);

  if (!account || !post || !type) {
    return null;
  }

  const renderHeader = () => (
    <Box>
      <Flex sx={{ alignItems: "center", padding: "12px 16px 0" }}>
        <Box
          sx={{
            position: "relative",
            width: "40px",
            height: "40px",
            lineHeight: "40px",
          }}
        >
          <SocialAccountAvatar
            sx={{
              width: "full",
              height: "full",
              fontSize: "16px",
              borderRadius: "full",
            }}
            name={account.name}
            src={account.image}
            socialType={account.social_type}
          />
        </Box>
        <Box sx={{ flex: "1 1 0%", margin: "0 10px" }}>
          <Box
            sx={{
              margin: "5px 0",
              fontSize: "15px",
              fontWeight: "600",
              lineHeight: "1.3333",
              color: "#050505",
            }}
          >
            <Box>{account.name}</Box>
          </Box>
          <Flex
            sx={{
              margin: "5px 0",
              fontSize: "13px",
              fontWeight: "400",
              lineHeight: "1.2308",
              color: "#65676b",
            }}
          >
            <Box>{time}</Box>
            <Box _before={{ content: '"\\2022"', margin: "0 3px" }}>
              <Icon as={BiWorld} />
            </Box>
          </Flex>
        </Box>
        {/* <Box sx={{ marginLeft: "auto" }}>
          <Box>
            {postMenu ? (
              postMenu
            ) : (
              <Box
                sx={{ fontSize: "16px", color: "#606770" }}
                _before={{ content: '"\\2022\\2022\\2022"' }}
              />
            )}
          </Box>
        </Box> */}
      </Flex>
    </Box>
  );

  const renderFooter = () => {
    const actionStyles = {
      alignItems: "center",
      justifyContent: "center",
      flex: "1 1 0%",
      padding: "6px 2px",
    };
    const actionIconStyles = {
      padding: "6px 4px",
      fontSize: "15px",
      fontWeight: "600",
      lineHeight: "1.3333",
      color: "#65676b",
      boxSizing: "content-box",
      width: "18px",
      height: "18px",
      fill: "currentColor",
    };
    const actionTextStyles = {
      padding: "6px 4px",
      fontSize: "15px",
      fontWeight: "600",
      lineHeight: "1.3333",
      color: "#65676b",
    };

    return (
      <Box>
        <Box sx={{ padding: "0 16px" }}>
          <Flex
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: "1px solid #ced0d4",
            }}
          >
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 24 24">
                <path
                  // eslint-disable-next-line max-len
                  d="M 23.28075,13.336686 C 23.746078,12.730106 24,11.938121 24,10.97072 23.999953,9.1956451 22.496578,7.5514131 20.590922,7.5514131 H 17.302641 C 17.38306,6.6918369 17.533976,5.8005601 17.533976,4.344371 17.534023,1.5923779 16.236582,0 13.442457,0 12.036442,0 11.432594,1.2915368 11.165265,2.6725278 11.005562,3.4975886 10.595955,4.7031764 10.438998,5.3436316 10.065299,6.8686273 8.625,8.9226508 7.6116563,9.4703827 7.5090469,9.5138243 7.3779375,9.548031 7.2351563,9.5751231 6.9645469,9.1810389 6.5123906,8.9226508 6,8.9226508 H 1.5 C 0.67157813,8.9226508 0,9.597692 0,10.430386 V 22.492265 C 0,23.324959 0.67157812,24 1.5,24 H 6 c 0.8284219,0 1.5,-0.675041 1.5,-1.507735 v -0.4118 c 1.5232031,0 4.596579,0.517266 8.195079,0.516277 0.258047,1.41e-4 0.700487,0.124135 2.168732,0.123758 1.468246,-3.77e-4 4.20133,-0.412037 4.11133,-2.951911 0.709078,-0.835238 1.056515,-2.041897 0.854296,-3.161767 0.583876,-0.920001 0.709032,-2.217784 0.451313,-3.270136 z M 1.5,22.492265 V 10.430386 H 6 V 22.492265 Z M 21.375797,12.89704 c 0.749203,0.548816 0.749203,2.810418 -0.262828,3.329503 0.633843,1.072376 0.06338,2.507363 -0.703828,2.921048 0.388734,2.475512 -1.584556,2.055845 -2.558689,2.065222 -0.974133,0.0094 -1.992201,-0.123759 -2.155326,-0.123759 -3.418547,0 -6.1929072,-0.516324 -8.1950791,-0.516324 v -9.594612 c 1.7675625,0 3.8583071,-3.0819354 4.3954491,-5.2738484 0.484319,-1.9762312 0.664268,-3.5624347 1.567529,-4.155753 2.273496,-0.2629872 2.571045,1.5909387 2.571045,2.7959016 0,1.9878069 -0.301704,3.5421968 -0.693054,4.7147769 h 5.249953 c 1.065469,0 1.90439,0.9594849 1.909078,1.9189229 0.0046,0.959437 -0.375047,1.71387 -1.12425,1.918922 z"
                  fill="currentColor"
                />
              </Icon>
              <Box sx={actionTextStyles}>{t("post_preview.facebook.like")}</Box>
            </Flex>
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 24 24">
                <path
                  // eslint-disable-next-line max-len
                  d="M 20.287425,0.02604515 3.5132634,2.9549051e-6 C 1.8613272,-0.00256171 0.04511798,1.6647695 0.03987518,3.3194486 L 1.0776331e-5,15.90104 C -0.00522922,17.55572 1.901487,19.468605 3.5531278,19.500001 l 2.1006598,0.03993 v 3.937499 c 0,0.332813 0.2731651,0.522569 0.5633072,0.522569 0.1123131,0 0.2161124,-0.0026 0.3305178,-0.07257 L 12.392573,19.53993 20.287425,19.5 C 21.939343,19.4917 24,17.595657 24,15.94097 V 3.2395871 C 24,1.5848997 21.939362,0.02860982 20.287425,0.02604515 Z M 22.221709,15.940971 c 0,0.694852 -1.043883,1.773667 -1.934284,1.77778 l -8.643606,0.03993 -0.599003,0.45 -3.6127378,2.831249 V 17.758682 L 3.5531278,17.718751 C 2.8310058,17.711317 1.7760999,16.595798 1.7783013,15.90104 L 1.8181657,3.3194486 c 0.00202,-0.638378 0.9731248,-1.5393167 1.6950977,-1.5381958 l 16.7741616,0.026043 c 0.778263,0.00121 1.934284,0.7092038 1.934284,1.432292 z"
                  fill="currentColor"
                />
              </Icon>
              <Box sx={actionTextStyles}>
                {t("post_preview.facebook.comment")}
              </Box>
            </Flex>
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 24 24">
                <path
                  // eslint-disable-next-line max-len
                  d="M 23.501357,9.2546092 16.887224,1.2436997 C 15.736992,0.04120622 13.677438,0.8421854 13.677438,2.5316197 V 6.0788266 C -0.65436685,7.3472127 -0.34883285,20.406924 0.17979732,22.253642 0.56463687,23.598042 2.4557523,23.728083 2.9933538,22.404314 5.44003,16.379713 8.717373,14.786922 13.677438,15.021603 v 3.532581 c 0,1.68799 2.058343,2.491717 3.209786,1.28792 l 6.614179,-8.011655 c 0.645827,-0.70174 0.683534,-1.7479 -4.6e-5,-2.5758398 z m -1.077242,1.5455038 -6.614273,8.011655 c -0.229775,0.24035 -0.641864,0.08058 -0.641864,-0.257584 V 13.52361 C 10.02986,13.28189 4.9330711,13.674245 1.6194268,21.835037 1.0474674,19.836756 1.9820977,7.3325191 15.167978,7.5614485 V 2.5308744 c 0,-0.3382594 0.412274,-0.4979336 0.641864,-0.257584 l 6.614273,8.0116546 c 0.103277,0.178931 0.103277,0.350267 0,0.515168 z"
                  fill="currentColor"
                />
              </Icon>
              <Box sx={actionTextStyles}>
                {t("post_preview.facebook.share")}
              </Box>
            </Flex>
          </Flex>
        </Box>
      </Box>
    );
  };

  const renderMessage = () => (
    <Box sx={{ margin: "4px 16px 0" }}>
      <Box
        sx={{
          fontSize: "15px",
          fontWeight: "400",
          lineHeight: "1.3333",
          color: "#050505",
          marginBottom: "16px",
          _empty: {
            marginBottom: "0",
          },
          "& a": {
            color: "#216fdb",
          },
        }}
        dangerouslySetInnerHTML={{
          __html: formatPostMessage(post.message),
        }}
      />
    </Box>
  );

  const renderMedia = () => {
    const maxItems = 5;
    const mediaItems = getFixedPhotosVideosOrder(
      post?.photos,
      post?.videos
    ).items;
    const mediaItemsSliced = mediaItems.slice(0, maxItems);
    const isSingle = mediaItems.length === 1;
    const isNaturalSize = isSingle && isMediaHorizontal;

    const mediaItemPositions = {
      1: {
        1: "0 0 0 0",
      },
      2: {
        1: "0 0 calc(50% + 1.01px)",
        2: "calc(50% + 1.01px) 0 0",
      },
      3: {
        1: "0 0 calc(50% + 1.01px)",
        2: "calc(50% + 1.01px) calc(50% + 1.01px) 0 0",
        3: "calc(50% + 1.01px) 0 0 calc(50% + 1.01px)",
      },
      4: {
        1: "0 0 calc(33.3333% + 1.01px)",
        2: "calc(66.6667% + 1.01px) calc(66.6667% + 1.01px) 0 0",
        3: "calc(66.6667% + 1.01px) calc(33.3333% + 1.01px) 0",
        4: "calc(66.6667% + 1.01px) 0 0 calc(66.6667% + 1.01px)",
      },
      5: {
        1: "0 calc(50% + 1.01px) calc(40% + 1.01px) 0",
        2: "0 0 calc(40% + 1.01px) calc(50% + 1.01px)",
        3: "calc(60% + 1.01px) calc(66.6667% + 1.01px) 0 0",
        4: "calc(60% + 1.01px) calc(33.3333% + 1.01px) 0",
        5: "calc(60% + 1.01px) 0 0 calc(66.6667% + 1.01px)",
      },
    };

    const mediaItemStyles = {
      display: "inline-block",
      position: isNaturalSize ? "relative" : "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      verticalAlign: "center",
      objectFit: isSingle ? "contain" : "cover",
    };

    return (
      <Box>
        <Box
          sx={{
            position: "relative",
            paddingTop: isNaturalSize
              ? "0"
              : mediaItemsSliced.length === 5
              ? "83.3333%"
              : "100%",
            height: isNaturalSize ? "initial" : "0",
            marginBottom: "-1px",
          }}
        >
          {mediaItemsSliced.map((item, index) => (
            <Box
              key={item.data.url}
              sx={{
                position: isNaturalSize ? "relative" : "absolute",
              }}
              style={{
                inset:
                  mediaItemPositions?.[mediaItemsSliced.length]?.[index + 1] ||
                  null,
              }}
              _after={
                mediaItems.length > mediaItemsSliced.length &&
                mediaItemsSliced.length === index + 1
                  ? {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "absolute",
                      inset: "0",
                      content: `"+${
                        mediaItems.length - mediaItemsSliced.length + 1
                      }"`,
                      fontSize: "32px",
                      lineHeight: "1.34",
                      fontWeight: "600",
                      color: "#ffffff",
                      background: "rgba(0, 0, 0, 0.4)",
                    }
                  : null
              }
            >
              <Box
                sx={{
                  position: "relative",
                  padding: "0",
                  width: "full",
                  height: "full",
                  overflow: "hidden",
                  background: "#dddddd",
                }}
                _after={{
                  position: "absolute",
                  inset: "0",
                  content: '""',
                  pointerEvents: "none",
                  borderColor: "rgba(0, 0, 0, 0.15)",
                  borderStyle: "solid",
                  borderWidth: "1px 0",
                }}
              >
                {item.field === "videos" ? (
                  <Box
                    as="video"
                    sx={mediaItemStyles}
                    src={item.data.url}
                    controls
                    preload="metadata"
                    poster={item.data.thumbUrl}
                  />
                ) : (
                  <Image sx={mediaItemStyles} src={item.data.url} />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderLink = () => {
    const url = getUrl(post?.scraper?.link || "");
    const hasScraperImage = !!post?.scraper?.image;

    return (
      <Box>
        <LinkBox
          sx={{
            marginBottom: "-1px",
            display: isSmallScraperImage ? "flex" : null,
            alignItems: isSmallScraperImage ? "stretch" : null,
            borderTop: isSmallScraperImage ? "1px solid #ced0d4" : null,
            borderBottom: isSmallScraperImage ? "1px solid #ced0d4" : null,
          }}
        >
          {hasScraperImage ? (
            <Box
              sx={{
                width: isSmallScraperImage ? "140px" : null,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  paddingTop: isSmallScraperImage ? "100%" : "52.2%",
                  height: "0",
                  overflow: "hidden",
                }}
                _after={{
                  position: "absolute",
                  inset: "0",
                  content: '""',
                  borderColor: "rgba(0, 0, 0, 0.15)",
                  borderStyle: "solid",
                  borderWidth: isSmallScraperImage ? "0 1px 0 0" : "1px 0",
                }}
              >
                <Image
                  sx={{
                    position: "absolute",
                    inset: "0",
                    width: "full",
                    height: "full",
                    objectFit: "cover",
                  }}
                  src={post.scraper.image}
                />
              </Box>
            </Box>
          ) : null}
          <LinkOverlay
            sx={{
              display: isSmallScraperImage ? "flex" : "block",
              alignItems: isSmallScraperImage ? "center" : null,
              width:
                hasScraperImage && isSmallScraperImage
                  ? "calc(100% - 140px)"
                  : "100%",
              padding: "8px 16px",
              background: "#f0f2f5",
              borderBottom: isSmallScraperImage ? null : "1px solid #ced0d4",
            }}
            href={post.scraper.link}
            isExternal
          >
            <Flex width="full" direction="column">
              <Text
                sx={{
                  fontSize: "13px",
                  fontWeight: "normal",
                  lineHeight: "1.2308",
                  textTransform: "uppercase",
                  color: "#65676b",
                  margin: "0 0 3px",
                }}
                noOfLines={1}
              >
                {url?.hostname || ""}
              </Text>
              {post.scraper.title ? (
                <Text
                  sx={{
                    fontSize: "17px",
                    fontWeight: "600",
                    lineHeight: "1.3333",
                    color: "#050505",
                  }}
                  noOfLines={isSmallScraperImage ? 2 : 1}
                >
                  {post.scraper.title}
                </Text>
              ) : null}
              {post.scraper.description ? (
                <Text
                  sx={{
                    fontSize: "15px",
                    fontWeight: "normal",
                    lineHeight: "1.3333",
                    color: "#65676b",
                  }}
                  noOfLines={isSmallScraperImage ? 3 : 1}
                >
                  {post.scraper.description}
                </Text>
              ) : null}
            </Flex>
          </LinkOverlay>
        </LinkBox>
      </Box>
    );
  };

  const renderCarousel = () => {
    const carouselFirstItemActive = carouselActiveIndex === 0;
    const carouselLastItemActive =
      carouselActiveIndex === carouselItems.length - 1;
    const carouselInnerWidth = carouselListBoxModel?.contentBox?.width;
    const carouselItemWidth =
      carouselInnerWidth < 310 ? carouselInnerWidth : 310;
    // eslint-disable-next-line no-nested-ternary
    const carouselMargin = carouselFirstItemActive
      ? 0
      : carouselLastItemActive
      ? carouselInnerWidth - carouselItemWidth
      : (carouselInnerWidth - carouselItemWidth) / 2;
    const carouselTranslateX =
      (carouselActiveIndex * carouselItemWidth - carouselMargin) * -1;

    const carouselNavStyles = {
      position: "absolute",
      top: "50%",
      zIndex: "1",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "48px",
      height: "48px",
      transform: "translateY(-50%)",
      backgroundColor: "#ffffff",
      borderRadius: "50%",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      transition: "opacity 200ms cubic-bezier(.08,.52,.52,1), visibility 1.5s",
      color: "#65676b",
      fontSize: "36px",
      cursor: "pointer",
    };

    const carouselTitleStyles = {
      color: "#050505",
      fontSize: "15px",
      fontWeight: "600",
      lineHeight: "1.3333",
    };
    const carouselDescriptionStyles = {
      color: "#65676b",
      fontSize: "13px",
      lineHeight: "1.2308",
    };

    return (
      <Box>
        <Box
          ref={carouselInnerRef}
          sx={{
            position: "relative",
            zIndex: "1",
            marginBottom: "-1px",
            borderBottom: "1px solid #ffffff",
            overflow: "hidden",
            padding: "0 12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              transform: `translateX(${carouselTranslateX}px)`,
              transition: "transform 200ms ease-in-out",
            }}
          >
            {carouselItems.map((item) => (
              <Box
                key={item.$id}
                sx={{
                  padding: "0 4px",
                  width: `${carouselItemWidth}px`,
                  minWidth: `${carouselItemWidth}px`,
                }}
              >
                <LinkBox
                  as={Box}
                  sx={{
                    position: "relative",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <AspectRatio ratio={1}>
                      <Box>
                        <AspectRatio
                          ratio={1}
                          width={item.isEndCard ? "180px" : "100%"}
                        >
                          <Image
                            src={item.image}
                            alt={item.title}
                            sx={{ objectFit: "cover" }}
                          />
                        </AspectRatio>
                      </Box>
                    </AspectRatio>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: "0",
                        boxShadow: "inset 0 -1px 0 0 rgba(0, 0, 0, 0.1)",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      height: "73px",
                    }}
                  >
                    <LinkOverlay
                      href={item.link}
                      isExternal
                      sx={{ padding: "7px 12px" }}
                    >
                      {item.isEndCard ? (
                        <>
                          <Text
                            isTruncated
                            sx={{ ...carouselTitleStyles, textAlign: "center" }}
                          >
                            {t("post_preview.facebook.carousel.see_more_at")}
                          </Text>
                          <Text
                            isTruncated
                            sx={{
                              ...carouselTitleStyles,
                              textAlign: "center",
                              textTransform: "uppercase",
                            }}
                          >
                            {item.host}
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text isTruncated sx={carouselTitleStyles}>
                            {item.title}
                          </Text>
                          <Text isTruncated sx={carouselDescriptionStyles}>
                            {item.description}
                          </Text>
                        </>
                      )}
                    </LinkOverlay>
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: "0",
                      borderRadius: "8px",
                      boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.1)",
                      pointerEvents: "none",
                    }}
                  />
                </LinkBox>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              ...carouselNavStyles,
              left: "8px",
              opacity: carouselFirstItemActive ? "0" : "1",
              visibility: carouselFirstItemActive ? "hidden" : null,
            }}
            onClick={carouselFirstItemActive ? null : handleCarouselPrevClick}
          >
            <Icon as={BiChevronLeft} />
          </Box>
          <Box
            sx={{
              ...carouselNavStyles,
              right: "8px",
              opacity: carouselLastItemActive ? "0" : "1",
              visibility: carouselLastItemActive ? "hidden" : null,
            }}
            onClick={carouselLastItemActive ? null : handleCarouselNextClick}
          >
            <Icon as={BiChevronRight} />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderComments = () => {
    const comments = (post?.comments || []).map((item, index) => ({
      ...item,
      id: index,
    }));

    if (!comments.length) {
      return null;
    }

    return (
      <Box sx={{ padding: "0 16px" }}>
        <Box sx={{ padding: "0 0 8px", borderTop: "1px solid #ced0d4" }}>
          {comments.map((item) => (
            <Flex key={item.id} sx={{ margin: "8px 0 0" }}>
              <Box
                sx={{
                  position: "relative",
                  width: "32px",
                  height: "32px",
                  lineHeight: "32px",
                  borderRadius: "50%",
                  margin: "2px 6px 0 0",
                }}
              >
                <SocialAccountAvatar
                  sx={{ width: "full", height: "full", fontSize: "16px" }}
                  name={account.name}
                  src={account.image}
                  socialType={account.social_type}
                />
              </Box>
              <Box sx={{ flex: "1" }}>
                <Box
                  sx={{
                    display: "inline-block",
                    padding: "8px 12px",
                    backgroundColor: "#f0f2f5",
                    borderRadius: "18px",
                    color: "#050505",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "13px",
                      lineHeight: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {account.name}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "15px",
                      lineHeight: "19px",
                      "& a": {
                        color: "#216fdb",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatPostMessage(item.message),
                    }}
                  />
                </Box>
                <Flex
                  sx={{
                    padding: "3px 12px",
                    fontSize: "12px",
                    lineHeight: "12px",
                    color: "#65676b",
                  }}
                >
                  <Box sx={{ fontWeight: "bold" }}>
                    {t("post_preview.facebook.like")}
                  </Box>
                  <Box>&nbsp;·&nbsp;</Box>
                  <Box sx={{ fontWeight: "bold" }}>
                    {t("post_preview.facebook.reply")}
                  </Box>
                  <Box>&nbsp;·&nbsp;</Box>
                  <Box>{`+${item.delay || 0} min`}</Box>
                </Flex>
              </Box>
            </Flex>
          ))}
        </Box>
      </Box>
    );
  };

  const showMedia = !!post?.videos?.length || !!post?.photos?.length;
  const showLink = !showMedia && !carousel && post?.scraper?.link;
  const showCarousel = !showMedia && !!carousel;

  return (
    <Box
      sx={{
        background: "white",
        fontFamily:
          '"Segoe UI Historic", "Segoe UI", Helvetica, Arial, sans-serif',
        boxShadow: "rgba(0, 0, 0, 0.2) 0px 0px 3px 0px",
        borderRadius: "8px",
        maxWidth: "500px",
      }}
      {...props}
    >
      <Box>
        {renderHeader()}
        <Box>
          <Box>
            {renderMessage()}
            {showMedia ? renderMedia() : null}
            {showLink ? renderLink() : null}
            {showCarousel ? renderCarousel() : null}
          </Box>
        </Box>
        {renderFooter()}
        {renderComments()}
      </Box>
    </Box>
  );
};

SocialPostCardFacebook.propTypes = {
  account: SocialAccountPropType.isRequired,
  post: PostPropType.isRequired,
  time: PropTypes.string,
};

export default SocialPostCardFacebook;
